/**
 * Die gemeinsame Lieferantenschnittstelle.
 *
 * Der Rest von FORQ kennt weder TVH- noch TREX-Nutzlasten. Alles Lieferantenspezifische wird
 * INNERHALB des jeweiligen Adapters in die Formen dieser Datei übersetzt -- das ist die einzige
 * Sprache, die nach außen dringt.
 *
 * Dieselbe Schnittstelle gibt es zweimal, und das ist Absicht:
 *
 *   hier (TypeScript)  für den Übungslieferanten im Demo-Betrieb und für die Typen, mit denen
 *                      die Oberfläche den FORQ-Server anspricht.
 *   im FORQ-Server     als `ILieferantAdapter` (C#) -- dort liegen die Zugangsdaten und dort
 *                      findet der echte ausgehende Ruf statt. Nur dort.
 *
 * Die Namen sind absichtlich gleich gehalten; wer eine Seite ändert, findet die andere.
 */

import type {
  PartsAvailability,
  PartsDeliveryAddress,
  ProcurementKind,
  SupplierProvider,
} from './types';

/**
 * Wie ein ausgehender Ruf ausgegangen ist.
 *
 * `unknown` ist der wichtigste der drei Werte und der Grund, warum hier kein blanker
 * `throw` steht: bei Zeitüberschreitung oder Verbindungsabbruch WEISS niemand, ob der
 * Lieferant die Bestellung bekommen hat. Dieser Fall darf nicht als Fehlschlag durchgehen,
 * sonst bestellt der nächste Versuch ein zweites Mal.
 */
export type SupplierCallOutcome = 'ok' | 'error' | 'unknown';

export interface SupplierCallResult<T> {
  readonly outcome: SupplierCallOutcome;
  /** Reist bei jedem ausgehenden Ruf mit und steht in jedem Protokolleintrag beider Seiten. */
  readonly correlationId: string;
  readonly data: T | null;
  /** Grobe Einordnung für die Klärfallliste: `timeout`, `auth`, `http-500`, `contract`, … */
  readonly errorKind: string;
  /** Klartext für den Benutzer. Enthält nie technische Geheimnisse. */
  readonly message: string;
  /** Bereinigte Metadaten fürs Protokoll -- niemals Marken, Passwörter oder Sitzungswerte. */
  readonly metadata: Readonly<Record<string, unknown>>;
}

/** Was ein Lieferant kann. Was fehlt, bleibt in der Oberfläche abgeschaltet statt zu scheitern. */
export interface SupplierCapabilities {
  readonly punchout: boolean;
  readonly electronicOrdering: boolean;
  readonly priceCheck: boolean;
  readonly orderStatus: boolean;
  readonly deliveryInfo: boolean;
  readonly invoiceInfo: boolean;
  readonly findByCorrelationId: boolean;
}

/**
 * Eine dokumentierte Lücke.
 *
 * Statt einen Endpunkt zu erfinden, trägt der Adapter ein, WAS ihm fehlt. Die Oberfläche und
 * `docs/integration/TVH-TREX-CHECKLISTE.md` lesen dasselbe -- so kann keine Lücke stillschweigend
 * verschwinden.
 */
export interface SupplierIntegrationGap {
  readonly topic: string;
  readonly question: string;
}

// ---------------------------------------------------------------------------
// PunchOut
// ---------------------------------------------------------------------------

export interface CreatePunchoutSessionRequest {
  readonly provider: SupplierProvider;
  readonly tenantId: string;
  readonly workOrderId: string;
  readonly workOrderNumber: string;
  readonly userId: string;
  readonly userName: string;
  readonly correlationId: string;
  /** Adresse, an die der Lieferant den Warenkorb zurückschickt. Vom Server gebildet, nie vom Browser. */
  readonly returnUrl: string;
  /** Der Einmalwert der Sitzung. Verlässt den Server nur in Richtung Lieferant. */
  readonly sessionToken: string;
  readonly expiresAt: string;
  /** Fehlmengen, mit denen der Warenkorb vorbelegt werden soll -- sofern der Lieferant das unterstützt. */
  readonly requestedItems: readonly PunchoutRequestedItem[];
}

export interface PunchoutRequestedItem {
  readonly supplierItemNumber: string;
  readonly manufacturerPartNumber: string;
  readonly description: string;
  readonly quantity: number;
}

export interface CreatePunchoutSessionResponse {
  /** Adresse, die der Browser öffnet. */
  readonly startUrl: string;
  /** Was der Lieferant als Sitzungskennung führt -- für die spätere Zuordnung der Rückgabe. */
  readonly supplierSessionId: string;
}

/** Die Rückgabe des Warenkorbs, roh, wie sie beim Server ankommt. */
export interface PunchbackEnvelope {
  readonly provider: SupplierProvider;
  readonly contentType: string;
  readonly rawBody: string;
  readonly headers: Readonly<Record<string, string>>;
  /** Der Einmalwert aus der Rückgabeadresse -- vom Server ausgelesen, bevor der Adapter sie sieht. */
  readonly sessionToken: string;
}

export interface PunchbackResult {
  readonly sessionToken: string;
  readonly supplierSessionId: string;
  readonly currency: string;
  readonly shippingCost: number;
  readonly shippingMethod: string;
  readonly lines: readonly SupplierCartLine[];
}

/** Eine Warenkorbzeile, bereits auf FORQ-Form gebracht. */
export interface SupplierCartLine {
  readonly supplierItemNumber: string;
  readonly manufacturer: string;
  readonly manufacturerPartNumber: string;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  readonly currency: string;
  readonly availability: PartsAvailability;
  readonly availabilityText: string;
  readonly leadTimeDays: number | null;
  readonly supplierReference: string;
}

// ---------------------------------------------------------------------------
// Preis und Verfügbarkeit
// ---------------------------------------------------------------------------

export interface PriceCheckRequest {
  readonly provider: SupplierProvider;
  readonly correlationId: string;
  readonly items: readonly {
    readonly lineId: string;
    readonly supplierItemNumber: string;
    readonly quantity: number;
  }[];
}

export interface PriceCheckLine {
  readonly lineId: string;
  readonly supplierItemNumber: string;
  readonly unitPrice: number;
  readonly currency: string;
  readonly availability: PartsAvailability;
  readonly availabilityText: string;
  readonly leadTimeDays: number | null;
}

export interface PriceCheckResponse {
  readonly lines: readonly PriceCheckLine[];
  readonly shippingCost: number;
  readonly shippingMethod: string;
  readonly currency: string;
}

// ---------------------------------------------------------------------------
// Bestellung
// ---------------------------------------------------------------------------

export interface SubmitSupplierOrderRequest {
  readonly provider: SupplierProvider;
  /** Interne Bestellnummer -- reist als Kundenreferenz mit, damit der Lieferant sie auf Beleg und Rechnung führt. */
  readonly purchaseOrderNumber: string;
  readonly correlationId: string;
  /** Derselbe Wert wie in der Datenbank. Lieferanten, die Idempotenz unterstützen, bekommen ihn mit. */
  readonly idempotencyKey: string;
  readonly procurementKind: ProcurementKind;
  /** Nur zur Referenz auf dem Beleg -- der Lieferant erfährt keine Kundendaten. */
  readonly workOrderNumber: string;
  readonly currency: string;
  readonly deliveryAddress: PartsDeliveryAddress;
  readonly shippingMethod: string;
  readonly lines: readonly {
    readonly lineId: string;
    readonly lineNumber: number;
    readonly supplierItemNumber: string;
    readonly description: string;
    readonly quantity: number;
    readonly unit: string;
    readonly unitPrice: number;
  }[];
}

export interface SubmitSupplierOrderResponse {
  readonly externalOrderNumber: string;
  readonly acceptedAt: string;
  readonly shippingCost: number;
  readonly currency: string;
}

export type SupplierOrderState =
  | 'received'
  | 'confirmed'
  | 'partially-shipped'
  | 'shipped'
  | 'cancelled'
  | 'unknown';

export interface SupplierOrderStatusResponse {
  readonly externalOrderNumber: string;
  readonly state: SupplierOrderState;
  readonly stateText: string;
  readonly lines: readonly {
    readonly supplierItemNumber: string;
    readonly orderedQuantity: number;
    readonly shippedQuantity: number;
    readonly expectedAt: string | null;
  }[];
}

export interface SupplierDeliveryInfo {
  readonly externalOrderNumber: string;
  readonly deliveryNoteNumber: string;
  readonly carrier: string;
  readonly trackingReference: string;
  readonly shippedAt: string | null;
}

export interface SupplierInvoiceInfo {
  readonly externalOrderNumber: string;
  readonly invoiceNumber: string;
  readonly invoiceDate: string | null;
  readonly netAmount: number;
  readonly grossAmount: number;
  readonly currency: string;
}

// ---------------------------------------------------------------------------
// Der Port
// ---------------------------------------------------------------------------

/**
 * Ein Lieferant, wie FORQ ihn sieht.
 *
 * Kein Aufruf wirft für einen fachlichen Fehlschlag -- jeder liefert ein `SupplierCallResult`
 * mit `outcome`. Nur ein Programmierfehler wirft.
 */
export interface SupplierAdapter {
  readonly provider: SupplierProvider;
  readonly capabilities: SupplierCapabilities;
  /** Was für die volle Anbindung noch fehlt. Leer, sobald alles dokumentiert und gebaut ist. */
  readonly gaps: readonly SupplierIntegrationGap[];

  createPunchoutSession(
    request: CreatePunchoutSessionRequest,
  ): Promise<SupplierCallResult<CreatePunchoutSessionResponse>>;

  /** Echtheit der Rückgabe prüfen UND auswerten -- beides gehört zusammen und darf nicht getrennt aufgerufen werden. */
  authenticateAndParsePunchback(
    envelope: PunchbackEnvelope,
  ): Promise<SupplierCallResult<PunchbackResult>>;

  checkPrices(request: PriceCheckRequest): Promise<SupplierCallResult<PriceCheckResponse>>;

  submitOrder(
    request: SubmitSupplierOrderRequest,
  ): Promise<SupplierCallResult<SubmitSupplierOrderResponse>>;

  fetchOrderStatus(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierOrderStatusResponse>>;

  fetchOrderConfirmation(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierOrderStatusResponse>>;

  fetchDeliveryInfo(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierDeliveryInfo>>;

  fetchInvoiceInfo(
    externalOrderNumber: string,
    correlationId: string,
  ): Promise<SupplierCallResult<SupplierInvoiceInfo>>;

  /**
   * Die Bestellung anhand der Korrelations-ID suchen.
   *
   * Der Ausweg aus `unknown-external-state`: bevor irgendetwas erneut gesendet wird, wird
   * gefragt, ob der Lieferant den Vorgang schon kennt. Kann ein Lieferant das nicht, meldet
   * `capabilities.findByCorrelationId = false` -- dann bleibt nur die Klärung von Hand.
   */
  findOrderByCorrelationId(
    correlationId: string,
  ): Promise<SupplierCallResult<SubmitSupplierOrderResponse>>;
}

/**
 * Ein Fehlschlag, der KEINER ist: der Lieferant hat vielleicht bestellt, vielleicht nicht.
 *
 * Kleiner Helfer, damit jeder Adapter denselben Wert bildet und die Statusmaschine sich auf
 * `outcome === 'unknown'` verlassen kann.
 */
export function unknownOutcome<T>(
  correlationId: string,
  errorKind: string,
  message: string,
  metadata: Readonly<Record<string, unknown>> = {},
): SupplierCallResult<T> {
  return { outcome: 'unknown', correlationId, data: null, errorKind, message, metadata };
}

export function errorOutcome<T>(
  correlationId: string,
  errorKind: string,
  message: string,
  metadata: Readonly<Record<string, unknown>> = {},
): SupplierCallResult<T> {
  return { outcome: 'error', correlationId, data: null, errorKind, message, metadata };
}

export function okOutcome<T>(
  correlationId: string,
  data: T,
  metadata: Readonly<Record<string, unknown>> = {},
): SupplierCallResult<T> {
  return { outcome: 'ok', correlationId, data, errorKind: '', message: '', metadata };
}

/**
 * Die Schlüssel, die aus jedem Protokoll und jeder Antwort verschwinden.
 *
 * Bewusst eine Liste von Teilzeichenketten und keine exakten Namen: kein Mensch weiß vorher,
 * ob ein Lieferant das Feld `apiKey`, `api_key` oder `X-Api-Key` nennt.
 */
export const secretFieldMarkers: readonly string[] = [
  'authorization',
  'password',
  'passwort',
  'secret',
  'token',
  'apikey',
  'api_key',
  'api-key',
  'credential',
  'cookie',
  'sessionid',
  'session_id',
  'signature',
  'hmac',
  'kennwort',
];

/**
 * Metadaten für das Protokoll bereinigen.
 *
 * Alles, dessen Name nach einem Geheimnis aussieht, wird ersetzt -- nicht gelöscht: dass ein
 * Feld DA war, ist für die Fehlersuche wertvoll, sein Wert nie.
 */
export function redactMetadata(
  metadata: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(metadata)) {
    const lower = key.toLowerCase().replace(/[\s-]/g, '');
    if (secretFieldMarkers.some((marker) => lower.includes(marker.replace(/[\s_-]/g, '')))) {
      clean[key] = '[entfernt]';
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      clean[key] = redactMetadata(value as Readonly<Record<string, unknown>>);
      continue;
    }
    clean[key] = value;
  }
  return clean;
}
